import * as microsoftTeams from '@microsoft/teams-js';

export interface TeamsContext {
  userId: string;
  displayName: string;
  userPrincipalName: string;
  tenantId: string;
  locale: string;
  theme: 'default' | 'dark' | 'contrast';
  hostName: string;
}

let initialized = false;
let inTeams = false;

async function initialize(): Promise<void> {
  if (initialized) return;
  await microsoftTeams.app.initialize();
  initialized = true;
}

/** usePlatform marca esto cuando confirma que corremos dentro de Teams. */
function markInTeams(): void {
  inTeams = true;
}

function isInTeams(): boolean {
  return inTeams;
}

/**
 * Token SSO de Teams (silencioso, sin redirect). aud = a2d1655c (la app del webApplicationInfo),
 * así que las APIs (que validan aud=a2d1655c) lo aceptan directo — sin OBO.
 * Requiere que a2d1655c pre-autorice los client IDs de Teams (paso de Sigma).
 */
async function getSsoToken(): Promise<string> {
  await initialize();
  return await microsoftTeams.authentication.getAuthToken();
}

async function getContext(): Promise<TeamsContext | null> {
  try {
    const ctx = await microsoftTeams.app.getContext();
    // Teams NO siempre trae displayName (a diferencia del navegador con MSAL). Si falta,
    // lo sacamos del claim 'name' del token SSO -> el mismo nombre completo de AAD que
    // muestra el web. Sin esto el saludo cae al correo.
    let displayName = ctx.user?.displayName ?? '';
    if (!displayName) displayName = await getSsoDisplayName();
    return {
      userId: ctx.user?.id ?? '',
      displayName,
      userPrincipalName: ctx.user?.userPrincipalName ?? '',
      tenantId: ctx.user?.tenant?.id ?? '',
      locale: ctx.app?.locale ?? 'es',
      theme: (ctx.app?.theme as TeamsContext['theme']) ?? 'default',
      hostName: ctx.app?.host?.name ?? 'web',
    };
  } catch {
    return null;
  }
}

/** Decodifica el claim 'name' (nombre completo AAD) del token SSO de Teams. Vacío si algo falla. */
async function getSsoDisplayName(): Promise<string> {
  try {
    const token = await microsoftTeams.authentication.getAuthToken();
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    // Decodifica UTF-8 correctamente (acentos: "Chávez").
    const json = decodeURIComponent(
      atob(b64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    const payload = JSON.parse(json) as { name?: string };
    return payload.name ?? '';
  } catch {
    return '';
  }
}

function notifyAppLoaded(): void {
  try { microsoftTeams.app.notifyAppLoaded(); } catch { /* not in Teams */ }
}

function notifySuccess(): void {
  try { microsoftTeams.app.notifySuccess(); } catch { /* not in Teams */ }
}

function registerOnThemeChangeHandler(handler: (theme: string) => void): void {
  try {
    microsoftTeams.app.registerOnThemeChangeHandler(handler);
  } catch { /* not in Teams */ }
}

const TeamsSDK = { initialize, getContext, notifyAppLoaded, notifySuccess, registerOnThemeChangeHandler, markInTeams, isInTeams, getSsoToken };
export default TeamsSDK;
